import { stores } from '@kurtosys/app-start';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';

import { IComponentConfigurations } from '../../../models/app/IComponentConfigurations.js';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { ApplyFiltersStore } from '../../ApplyFilters/stores/ApplyFiltersStore.js';
import { ChangeAttestationStore } from '../../ChangeAttestation/stores/ChangeAttestationStore.js';
import { ClearAllFiltersStore } from '../../ClearAllFilters/stores/ClearAllFiltersStore.js';
import { DatePickerStore } from '../../DatePicker/stores/DatePickerStore.js';
import { DropdownStore } from '../../Dropdown/stores/DropdownStore.js';
import { MegaMenuStore } from '../../MegaMenu/stores/MegaMenuStore.js';
import { MenuStore } from '../../Menu/stores/MenuStore.js';
import { IInputConfiguration } from '../models/IInputConfiguration.js';

import { AppStore } from './AppStore.js';

export class ComponentInputsStore extends stores.base.StoreBase<IConfiguration, IComponentStyles> {
	datePickerStores: Map<string, DatePickerStore> = new Map();
	dropdownStores: Map<string, DropdownStore> = new Map();
	menuStores: Map<string, MenuStore> = new Map();
	megaMenuStores: Map<string, MegaMenuStore> = new Map();
	changeAttestationStores: Map<string, ChangeAttestationStore> = new Map();
	applyFiltersStores: Map<string, ApplyFiltersStore> = new Map();
	clearAllFiltersStores: Map<string, ClearAllFiltersStore> = new Map();

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
	}

	get inputConfigurations(): IInputConfiguration[] {
		const { inputConfigurations = [] } = this.storeContext.get<AppStore>('appStore');
		return inputConfigurations;
	}

	async initialize(): Promise<void> {
		for (const inputConfiguration of this.inputConfigurations) {
			await this.initializeInputStore(inputConfiguration);
		}
	}

	async initializeInputStore(inputConfiguration: IInputConfiguration) {
		const { key, type, options } = inputConfiguration;
		if (options) {
			switch (type) {
				case 'DatePicker':
					if (options.datePicker) {
						const optionsComponentProps = options.datePicker.componentProps;
						const componentProps = this.getInputStoreComponentProps(
							DatePickerStore.componentKey,
							optionsComponentProps,
						);
						const store = new DatePickerStore(
							this.storeContext,
							this.storeContext.manifest,
							key,
							options.datePicker,
							componentProps,
						);
						await store.initialize();
						this.datePickerStores.set(key, store);
					}
					break;
				case 'Dropdown':
					if (options.dropdown) {
						const optionsComponentProps = options.dropdown.componentProps;
						const componentProps = this.getInputStoreComponentProps(
							DropdownStore.componentKey,
							optionsComponentProps,
						);
						const store = new DropdownStore(
							this.storeContext,
							this.storeContext.manifest,
							key,
							options.dropdown,
							componentProps,
						);
						await store.initialize();
						this.dropdownStores.set(key, store);
					}
					break;
				case 'Menu':
					if (options.menu) {
						const optionsComponentProps = options.menu.componentProps;
						const componentProps = this.getInputStoreComponentProps(
							MenuStore.componentKey,
							optionsComponentProps,
						);
						const store = new MenuStore(
							this.storeContext,
							this.storeContext.manifest,
							key,
							options.menu,
							componentProps,
						);
						await store.initialize();
						this.menuStores.set(key, store);
					}
					break;
				case 'MegaMenu':
					if (options.megaMenu) {
						const optionsComponentProps = options.megaMenu.componentProps;
						const componentProps = this.getInputStoreComponentProps(
							MegaMenuStore.componentKey,
							optionsComponentProps,
						);
						const store = new MegaMenuStore(
							this.storeContext,
							this.storeContext.manifest,
							key,
							options.megaMenu,
							componentProps,
						);
						await store.initialize();
						this.megaMenuStores.set(key, store);
					}
					break;
				case 'ChangeAttestation':
					if (options.changeAttestation) {
						const optionsComponentProps = options.changeAttestation.componentProps;
						const componentProps = this.getInputStoreComponentProps(
							ChangeAttestationStore.componentKey,
							optionsComponentProps,
						);
						const store = new ChangeAttestationStore(
							this.storeContext,
							this.storeContext.manifest,
							key,
							options.changeAttestation,
							componentProps,
						);
						await store.initialize();
						this.changeAttestationStores.set(key, store);
					}
					break;
				case 'ApplyFilters':
					if (options.applyFilters) {
						const optionsComponentProps = options.applyFilters.componentProps;
						const componentProps = this.getInputStoreComponentProps(
							ApplyFiltersStore.componentKey,
							optionsComponentProps,
						);
						const store = new ApplyFiltersStore(
							this.storeContext,
							this.storeContext.manifest,
							key,
							options.applyFilters,
							componentProps,
						);
						await store.initialize();
						this.applyFiltersStores.set(key, store);
					}
					break;
				case 'ClearAllFilters':
					if (options.clearAllFilters) {
						const optionsComponentProps = options.clearAllFilters;
						const componentProps = this.getInputStoreComponentProps(
							ClearAllFiltersStore.componentKey,
							optionsComponentProps,
						);
						const store = new ClearAllFiltersStore(
							this.storeContext,
							this.storeContext.manifest,
							key,
							options.clearAllFilters,
							componentProps,
						);
						await store.initialize();
						this.clearAllFiltersStores.set(key, store);
					}
					break;
			}
		}
	}

	getInputStoreComponentProps<K extends keyof Omit<IComponentConfigurations, 'app'>>(
		componentKey: K,
		componentProps: any,
	) {
		const appStore = this.storeContext.get<AppStore>('appStore');
		const componentConfiguration = appStore.getComponentConfiguration(componentKey);
		if (componentConfiguration) {
			return common.commonUtils.deepMergeObjects(componentConfiguration.componentProps, componentProps);
		}
		return componentProps;
	}
}
